import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export function PaymentSuccessPage() {
  const { api, refreshUser } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | paid | failed
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let active = true;
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        if (r.data.payment_status === "paid") {
          setState("paid");
          await refreshUser();
          toast.success("Pagamento confirmado!");
          return;
        }
        if (tries < 20) setTimeout(poll, 2000);
        else { setState("failed"); toast.error("Não conseguimos confirmar o pagamento. Tente novamente mais tarde."); }
      } catch {
        if (tries < 20) setTimeout(poll, 2000);
        else setState("failed");
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId, api, refreshUser]);

  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <div className="pnc-card p-8 max-w-md w-full text-center animate-slide-up">
        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-emerald-400 animate-spin mx-auto mb-4" />
            <h1 className="font-display text-2xl font-bold text-white">Confirmando pagamento…</h1>
            <p className="text-sm text-slate-400 mt-2">Isso leva alguns segundos.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500 flex items-center justify-center mb-4 shadow-xl shadow-emerald-500/40">
              <CheckCircle2 className="w-9 h-9 text-slate-900" strokeWidth={2.4} />
            </div>
            <h1 className="font-display text-2xl font-bold text-white">Pagamento confirmado!</h1>
            <p className="text-sm text-slate-300 mt-2">Seu acesso vitalício ao Põe na Conta está liberado.</p>
            <button
              data-testid="go-dashboard-btn"
              onClick={() => navigate("/", { replace: true })}
              className="btn-emerald w-full mt-6 py-3 rounded-xl flex items-center justify-center gap-2"
            >
              Ir para o painel <ArrowRight className="w-4 h-4" />
            </button>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle className="w-12 h-12 text-rose-400 mx-auto mb-4" />
            <h1 className="font-display text-2xl font-bold text-white">Não confirmamos o pagamento</h1>
            <p className="text-sm text-slate-400 mt-2">Se o valor foi cobrado, aguarde alguns minutos e recarregue. Se não, tente de novo.</p>
            <Link to="/pagar" className="btn-emerald inline-flex mt-6 py-3 px-6 rounded-xl items-center gap-2">Tentar novamente</Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancelPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <div className="pnc-card p-8 max-w-md w-full text-center animate-slide-up">
        <XCircle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
        <h1 className="font-display text-2xl font-bold text-white">Pagamento cancelado</h1>
        <p className="text-sm text-slate-400 mt-2">Nenhuma cobrança foi feita. Você pode tentar novamente quando quiser.</p>
        <button
          data-testid="cancel-back-btn"
          onClick={() => navigate("/pagar", { replace: true })}
          className="btn-emerald w-full mt-6 py-3 rounded-xl"
        >
          Voltar para a oferta
        </button>
      </div>
    </div>
  );
}
